import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Pagination } from '@core/models/pagination.model';
import { AppPermissionService } from '@core/services/app-permission.service';
import { CurrencyHttpService } from '@core/services/currency-http.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import * as moment from 'moment-timezone';
import { Observable, Subscription, of, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, exhaustMap, map, tap } from 'rxjs/operators';
import { AffiliateCampaignsDataService } from './services/affiliate-campaigns-data.service';

@Component({
  selector: 'kt-affiliate-campaigns',
  templateUrl: './affiliate-campaigns.component.html',
  styleUrls: ['./affiliate-campaigns.component.scss']
})
export class AffiliateCampaignsComponent implements OnInit, OnDestroy, AfterViewInit {

  form: FormGroup;
  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';
  dataLength = 0;
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  buttonLoading = false;
  dropdown = {
    currencies: JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies')),
    perPage: this.dropdownHttpService.perPage,
    // ['Inactive', 'Active'] — index maps 1:1 onto AffiliateCampaign::STATUS_INACTIVE / STATUS_ACTIVE
    statuses: this.dropdownHttpService.statuses,
  };

  // Promotion filter — same typeahead UX as 3.2.1 Promotion Codes: type to search, matches on
  // promotion code OR name, 300ms debounce, displayed as "CODE - Name".
  // Fed by /promotiondropdown rather than 3.2.1's /promotion/settings because that payload is
  // Promotion::select('code','name') with no id, and we filter on the indexed
  // affiliate_campaigns.promotion_id.
  promoTypeahead: any[] = [];
  // Affiliate multi-select (searchable, multiple). The component writes an array of the
  // selected ids into form control `affiliate_id`, which serialises to `affiliate_id=3,7,12`.
  affiliateDropdownList = [];
  affiliateSelectedItems = [];
  affiliateDropdownSettings = {
    singleSelection: false,
    text: 'Please Select',
    maxHeight: 200,
    enableFilterSelectAll: true,
    enableSearchFilter: true,
    classes: 'dropdown',
    primaryKey: 'id',
    labelKey: 'username',
    lazyLoading: true,
    noDataLabel: '',
    showCheckbox: true,
  };
  affiliateDropdownLoading = false;

  dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  ranges = this.transactionHttpService.ranges;
  affiliateCampaigns$: Observable<any[]>;
  messages$ = this.affiliateCampaignsDataService.messages$;

  sortingStorageName = 'sortingConfig';
  sortingStorageGroup = '10.9';
  default_sort_by = 'id';
  default_sort_order = 'desc';
  // Keys must match the API's sort_by whitelist in AffiliateCampaign::getBoUserData().
  // currency_code is intentionally absent — the Currency column is not sortable.
  sortingConfig = {
    'id': 'desc',
    'name': 'desc',
    'promotion_name': 'desc',
    'code': 'desc',
    'affiliate_username': 'desc',
    'status': 'desc',
    'created_by': 'desc',
    'updated_by': 'desc',
  };
  sortingSelection = {
    'sort_by': this.default_sort_by,
    'sort_order': this.default_sort_order
  };

  // permissions
  canExportAffiliateCampaigns: boolean;

  private subscriptions = new Subscription();

  constructor(
    public loadingBar: LoadingBarService,
    private dropdownHttpService: DropdownHttpService,
    private cdr: ChangeDetectorRef,
    private transactionHttpService: TransactionHttpService,
    private affiliateCampaignsDataService: AffiliateCampaignsDataService,
    private currencyHttpService: CurrencyHttpService,
    private appPermissionService: AppPermissionService,
  ) { }

  ngOnInit() {
    localStorage.setItem(this.sortingStorageName, JSON.stringify({ [this.sortingStorageGroup]: this.sortingConfig }));
    this.formInit();
    this.pagination = this.affiliateCampaignsDataService.pagination;
    this.onSubmit(true);
    this.setCurrencyDropdown();

    const promoSub = this.affiliateCampaignsDataService.getPromotions().subscribe(res => {
      this.promoTypeahead = res;
    });
    this.subscriptions.add(promoSub);

    this.affiliateDropdownLoading = true;
    const affSub = this.affiliateCampaignsDataService.getAffiliates().subscribe(
      res => {
        this.affiliateDropdownList = res;
        this.affiliateDropdownLoading = false;
      },
      // Reset on failure too — dataLoading drives a spinner inside
      // kt-dropdown-wo-lazyload, which would otherwise spin forever.
      // ApiErrorInterceptor already shows the error dialog.
      () => {
        this.affiliateDropdownLoading = false;
      }
    );
    this.subscriptions.add(affSub);

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canExportAffiliateCampaigns = appPermissions.export_affiliate_campaigns_list;
    });
    this.subscriptions.add(apSub);
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onSubmit(clearSearch?: boolean) {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.searchBtnLoading = clearSearch ? false : true;
    this.dataLength = 0;
    this.loading = true;
    of(this.buildFormValue()).pipe(
      map(this.filterFormFields),
      tap((data) => {
        if (data['start_datetime']) {
          data['start_datetime'] = moment(data['start_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_datetime'] = moment(data['end_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        const paramsRef = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.params = paramsRef;
        this.loadingBar.start();
        this.affiliateCampaigns$ = this.affiliateCampaignsDataService.getWithQuery(`?${this.params}&perPage=${this.pageSize}&${this.generateSortingParam()}`).pipe(
          tap(res => {
            this.dataLength = res.length;
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.page = 1;
            this.pagination = this.affiliateCampaignsDataService.pagination;
            this.loadingBar.complete();
          }),
          // Without this the table and the top progress bar stay stuck on any HTTP
          // failure. ApiErrorInterceptor already shows the error dialog; this only
          // clears the loading state so the page stays usable.
          catchError(err => {
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.loadingBar.complete();
            return throwError(err);
          })
        );
      }),
    ).subscribe();
  }

  onExport() {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.buttonLoading = true;
    of(this.buildFormValue()).pipe(
      map(this.filterFormFields),
      exhaustMap((data) => {
        if (data['start_datetime']) {
          data['start_datetime'] = moment(data['start_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_datetime'] = moment(data['end_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        data['paginate'] = false;
        data['timezone'] = this.timezone;

        // Local only — deliberately does NOT assign this.params. That field holds the LIST
        // filter string and is reused by onViewPageBy() / onPerPage() / onSortColumn();
        // leaking paginate=false into it makes the next paging request return the whole
        // unpaginated result set with no pagination envelope.
        const exportParams = Object.keys(data).map(key => key + '=' + data[key]).join('&');

        return this.affiliateCampaignsDataService.exportAffiliateCampaigns(`?${exportParams}&${this.generateSortingParam()}`).pipe(
          tap(() => {
            this.messages$ = this.affiliateCampaignsDataService.messages$;
            this.loadingBar.complete();
            this.buttonLoading = false;
          }),
          catchError(err => {
            this.loadingBar.complete();
            this.buttonLoading = false;
            return throwError(err);
          })
        );
      }),
    ).subscribe();
  }

  onClear() {
    this.clearBtnLoading = true;
    this.pageSize = 30;
    this.affiliateSelectedItems = [];
    this.form.patchValue({
      name: null,
      campaign_code: null,
      affiliate_id: null,
      currency_id: 'all',
      // Clear falls back to Active, not All — the list always opens on Active campaigns.
      status: '1',
      promotion: null,
      start_datetime: null,
      end_datetime: null,
      defaultDate: null,
      date_type: 'created_at',
    });
    this.onSubmit(true);
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    return this.affiliateCampaigns$ = this.affiliateCampaignsDataService.getWithQuery(`?page=${page}&perPage=${pageSize}${params}&${this.generateSortingParam()}`).pipe(
      tap(res => {
        this.dataLength = res.length;
        this.loading = false;
        this.pagination = this.affiliateCampaignsDataService.pagination;
        this.loadingBar.complete();
      }),
      // Reached from pagination, per-page and column sort — a failure here would
      // otherwise leave the table spinning until the operator reloads the page.
      catchError(err => {
        this.loading = false;
        this.loadingBar.complete();
        return throwError(err);
      })
    );
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_datetime: event.startDate !== null && event.startDate !== undefined ? event.startDate : null,
        end_datetime: event.endDate !== null && event.endDate !== undefined ? event.endDate : null
      });
    }
  }

  onClearDate() {
    if (this.form.value.start_datetime !== null) {
      this.form.patchValue({ defaultDate: null, start_datetime: null, end_datetime: null });
    }
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  onSortColumn(property: string) {
    // Reset other columns
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        if (key == property) {
          this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortingConfig[key] = 'desc';
        }
      }
    }

    // User selection
    this.sortingSelection.sort_by = property;

    if (this.sortingSelection.sort_by === property) {
      // Same column
      this.sortingSelection.sort_order = this.sortingConfig[property];
    } else {
      // Switch to other column
      this.sortingConfig[property] = 'asc';
      this.sortingSelection.sort_order = 'asc';
    }

    // Load Data
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  filterPromo(term: string): any[] {
    if (this.promoTypeahead) {
      return this.promoTypeahead.filter(
        promo => promo.code.toLowerCase().includes(term.toLowerCase()) || promo.name.toLowerCase().includes(term.toLowerCase())
      );
    } else {
      return [];
    }
  }

  searchPromo = (search$: Observable<string>): Observable<any[]> => {
    return search$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(term => term.trim() === '' ? [] : this.filterPromo(term))
    );
  }

  formatPromo(promotion: any): string {
    return `${promotion.code} - ${promotion.name}`;
  }

  private generateSortingParam() {
    return Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
  }

  /**
   * The Promotion typeahead holds the selected promotion OBJECT (or a raw string if the operator
   * typed without picking a suggestion). Convert it to the promotion_id the API expects, and never
   * forward the object itself as a query param.
   */
  private buildFormValue() {
    const formValue = { ...this.form.value };

    if (formValue.promotion) {
      if (typeof formValue.promotion === 'object' && formValue.promotion.id !== undefined) {
        // A suggestion was picked — filter on the indexed promotion_id.
        formValue.promotion_id = formValue.promotion.id;
      } else if (typeof formValue.promotion === 'string' && formValue.promotion.trim() !== '') {
        // Free text with no suggestion picked — fall back to a name search rather than
        // silently ignoring what the operator typed.
        formValue.promotion_name = formValue.promotion.trim();
      }
    }
    // Never forward the object itself as a query param.
    delete formValue.promotion;

    return formValue;
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && key !== 'defaultDate' && formData[key] !== 'all') {
        fields[key] = formData[key];
      }
    });
    return fields;
  }

  private formInit() {
    this.form = new FormGroup({
      name: new FormControl(null),
      campaign_code: new FormControl(null),
      affiliate_id: new FormControl(null),
      currency_id: new FormControl('all'),
      // Opens on Active, per the acceptance criteria ("status always choose active first").
      // String '1' (not numeric 1) so it matches the <option [value]="i"> string value and
      // preselects correctly — same as promotion-code.component.ts.
      status: new FormControl('1'),
      promotion: new FormControl(null),
      start_datetime: new FormControl(null),
      end_datetime: new FormControl(null),
      defaultDate: new FormControl(null), // Do not remove: For Clearing The Range
      date_type: new FormControl('created_at'),
    });
  }

  private setCurrencyDropdown() {
    if (this.dropdown.currencies.length === 0) {
      // set interval to get currencies from sessionStorage, to prevent returning empty currency dropdown
      if (sessionStorage.getItem('currencies') != undefined && sessionStorage.getItem('currencies') != null && sessionStorage.getItem('currencies') !== '') {
        var interval = setInterval(() => {
          this.dropdown.currencies = JSON.parse(sessionStorage.getItem('currencies'));
          if (this.dropdown.currencies != null) {
            clearInterval(interval);
          }
        }, 100);
      } else {
        this.currencyHttpService.setCurrency().subscribe(res => {
          this.dropdown.currencies = res;
        });
      }
    }
  }

}
